import type { PostEngagementCounts } from './feed.policy';

export const TRENDING_POST_WINDOW_HOURS = 72;
export const TRENDING_POST_MIN_WEIGHTED_ENGAGEMENT = 5;
export const TRENDING_POST_TOP_FRACTION = 0.1;
export const TRENDING_POST_MAX_BADGES = 20;

export type TrendingPostCandidate = {
  id: string;
  createdAt: Date;
  engagement: PostEngagementCounts;
};

export function computeWeightedPostEngagement(counts: PostEngagementCounts): number {
  return (
    Math.max(counts.likes ?? 0, 0) +
    Math.max(counts.comments ?? 0, 0) * 3 +
    Math.max(counts.bookmarks ?? 0, 0) * 4 +
    Math.max(counts.reposts ?? 0, 0) * 5
  );
}

/**
 * Post-level momentum score. Age decay prevents old lifetime totals from being
 * mistaken for a current trend. Author profile topics are intentionally absent.
 */
export function computeTrendingPostScore(
  candidate: Omit<TrendingPostCandidate, 'id'>,
  now = new Date(),
): number {
  const ageHours = Math.max(
    (now.getTime() - candidate.createdAt.getTime()) / (60 * 60 * 1000),
    0,
  );
  return computeWeightedPostEngagement(candidate.engagement) / Math.pow(ageHours + 2, 0.8);
}

/**
 * Selects the strongest recent posts using an adaptive top-decile threshold.
 * A small absolute engagement floor avoids labeling inactive posts as Trending.
 */
export function selectTrendingPostIds(
  candidates: TrendingPostCandidate[],
  now = new Date(),
): Set<string> {
  const windowStart = now.getTime() - TRENDING_POST_WINDOW_HOURS * 60 * 60 * 1000;
  const eligible = candidates
    .filter(
      (candidate) =>
        candidate.createdAt.getTime() >= windowStart &&
        candidate.createdAt.getTime() <= now.getTime() &&
        computeWeightedPostEngagement(candidate.engagement) >=
          TRENDING_POST_MIN_WEIGHTED_ENGAGEMENT,
    )
    .map((candidate) => ({
      id: candidate.id,
      score: computeTrendingPostScore(candidate, now),
    }))
    .sort((left, right) => right.score - left.score);

  if (eligible.length === 0) return new Set();

  const badgeCount = Math.min(
    TRENDING_POST_MAX_BADGES,
    Math.max(1, Math.ceil(eligible.length * TRENDING_POST_TOP_FRACTION)),
  );
  return new Set(eligible.slice(0, badgeCount).map((candidate) => candidate.id));
}

export function rankTrendingPosts<T extends TrendingPostCandidate>(
  candidates: T[],
  now = new Date(),
): T[] {
  return [...candidates].sort(
    (left, right) =>
      computeTrendingPostScore(right, now) - computeTrendingPostScore(left, now),
  );
}
